import { useSetQuantity } from "@frend-digital/centra/client";
import type {
  BaseSelection,
  BaseSelectionItem,
} from "@frend-digital/centra-types/selection";
import { events } from "@frend-digital/events";
import { gtag } from "@frend-digital/events/gtm";
import { tryCatch } from "@frend-digital/utils";
import clsx from "clsx";
import Image from "next/image";
import Button from "@/components/ui/Button";
import { useBeforeGtmEvent } from "@/features/global/Events";
import { useTranslations } from "@/i18n/client";
import type { FormattedCartItem } from "@/lib/centra/formatters";
import { klaviyoTracker } from "@/lib/klaviyo";
import { getCentraMedia } from "@/lib/utils/getCentraMedia";
import { selectionItemToGTAGProduct } from "@/lib/utils/gtm";
import QuantitySelector from "../QuantitySelector/QuantitySelector";
import styles from "./index.module.css";

// import useRemoveFromCart from "@/lib/centra/hooks/useSelection/cart/removeFromCart";

interface ICartItemProps {
  item: BaseSelectionItem | FormattedCartItem;
  isFinalized?: boolean;
}

const CartImage = ({ item }: ICartItemProps) => {
  const media =
    "product" in item
      ? getCentraMedia(item.product?.media, "full")
      : // eslint-disable-next-line @typescript-eslint/no-explicit-any
        getCentraMedia((item as any).media, "full");

  return (
    <div className={styles.image}>
      {media && (
        <Image
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          src={media?.href || (media?.href as any)?.href || ""}
          width={64}
          height={85}
          alt={""}
        />
      )}
    </div>
  );
};

const CartItemPrice = ({
  discountedPrice,
  price,
}: {
  discountedPrice?: string;
  price?: string;
}) => {
  const isDiscounted = discountedPrice !== price;
  return (
    <div className={clsx(styles.discounted__wrapper)}>
      {isDiscounted && (
        <span className={styles.discounted__price}>{discountedPrice}</span>
      )}
      <p className={styles.regular__price}>{price}</p>
    </div>
  );
};

const CartItemQuantity = ({ item }: ICartItemProps) => {
  if (!item) return null;

  return (
    <div className={styles.quantity__wrapper}>
      <QuantitySelector item={item} />
    </div>
  );
};

const DeleteCartItem = ({ item }: { item: BaseSelectionItem }) => {
  const { line } = item;
  const setQuantity = useSetQuantity({
    optimistic: true,
  });
  const beforeGtmEvent = useBeforeGtmEvent();

  if (!line) return null;

  const onSubtract = () => {
    tryCatch(
      setQuantity
        .mutateAsync(
          {
            line: line ?? "",
            quantity: 0,
          },
          {
            onSuccess: data => {
              klaviyoTracker.removeFromCart(
                data?.selection as BaseSelection,
                true,
                item,
              );
              if (
                data.selection?.currency &&
                data.selection?.totals.grandTotalPriceAsNumber
              ) {
                gtag.removeFromCart(
                  {
                    currency: data.selection.currency,
                    value: data.selection.totals.grandTotalPriceAsNumber,
                    items: (data.selection.items ?? []).map(item =>
                      selectionItemToGTAGProduct(
                        item,
                        data.selection!.currency!,
                      ),
                    ),
                  },
                  beforeGtmEvent,
                );
              }
            },
          },
        )
        .then(selection => {
          events.dispatch("remove_from_cart", selection, {
            dispatcher: "centra",
          });
        }),
    );
  };

  return (
    <Button mode="link" onClick={onSubtract}>
      <span>
        <span className={styles.removeLabel}>Remove</span>
      </span>
    </Button>
  );
};

const CartItem = ({ item, isFinalized }: ICartItemProps) => {
  const t = useTranslations();

  const isBaseSelectionItem = "product" in item;

  return (
    <div className={clsx(styles.item, isFinalized && styles.finalized)}>
      <CartImage item={item} />

      <div className={styles.details__container_wrap}>
        <div className={styles.item__info}>
          <div className={styles.details__container}>
            <div className={styles.left__column}>
              <p className={styles.item__name}>
                {isBaseSelectionItem
                  ? item.product?.name
                  : (item as FormattedCartItem).name}
              </p>
              <div className={styles.item__meta}>
                <span>{item.size}</span> /{" "}
                <span>
                  {isBaseSelectionItem
                    ? item.product?.variantName
                    : (item as FormattedCartItem).variant}
                </span>
              </div>
            </div>

            <div className={styles.right__column}>
              <CartItemPrice
                // TODO: Check sale price is working correctly
                discountedPrice={
                  isBaseSelectionItem
                    ? item.product?.priceBeforeDiscount
                    : (item as FormattedCartItem).discountedPrice
                }
                price={
                  isBaseSelectionItem
                    ? item.product?.price
                    : (item as FormattedCartItem).price
                }
              />
            </div>
          </div>
        </div>
        <div className={styles.item__actions}>
          {isFinalized ? (
            <span>
              {t("components.cartItem.quantity", { quantity: item.quantity })}
            </span>
          ) : (
            <>
              <CartItemQuantity item={item} />
              <DeleteCartItem item={item} />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CartItem;
